import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTags, faImage, faUpload, faTimes, 
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import { BackendChangeIndicator } from '../common/BackendChangeIndicator';

interface ArticleDetailMarketingProps {
  data: any;
  mode: 'view' | 'edit';
  onChange: (field: string, value: any) => void;
  isSupplierMode?: boolean;
  originalData?: any;
  articleNumber?: string;
}

const ArticleDetailMarketing: React.FC<ArticleDetailMarketingProps> = ({ 
  data, 
  mode, 
  onChange, 
  isSupplierMode = false,
  originalData,
  articleNumber
}) => {
  const [uploadingImage, setUploadingImage] = useState<string | null>(null);
  const mainImageInputRef = useRef<HTMLInputElement>(null);
  const backImageInputRef = useRef<HTMLInputElement>(null);
  const labelImageInputRef = useRef<HTMLInputElement>(null);

  const renderFieldWithHistory = (
    fieldName: string, 
    fieldPath: string,
    content: React.ReactNode
  ) => {
    // Change tracking only for Admin (not Supplier mode)
    if (isSupplierMode || !articleNumber) {
      return content;
    }
    
    return (
      <BackendChangeIndicator
        fieldName={fieldName}
        fieldPath={fieldPath}
        articleNumber={articleNumber}
      >
        {content}
      </BackendChangeIndicator>
    );
  };

  // Convert file to Base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  // Image upload handler - direkt Base64 speichern
  const handleImageUpload = async (file: File, imageType: string) => {
    setUploadingImage(imageType);
    
    try {
      console.log(`[MARKETING] Converting ${imageType} to Base64...`);
      const base64Image = await fileToBase64(file);
      
      // Update the correct field with the Base64 image
      if (imageType === 'main') {
        // Speichere in images.mainImage
        const updatedImages = { 
          ...(data.images || {}), 
          mainImage: base64Image 
        };
        onChange('images', updatedImages);
      } else if (imageType === 'back') {
        // Füge zu images.backImages hinzu
        const currentBackImages = data.images?.backImages || [];
        const updatedImages = { 
          ...(data.images || {}), 
          backImages: [...currentBackImages, base64Image] 
        };
        onChange('images', updatedImages);
      } else if (imageType === 'label') {
        // Füge zu images.labelImages hinzu
        const currentLabelImages = data.images?.labelImages || [];
        const updatedImages = { 
          ...(data.images || {}), 
          labelImages: [...currentLabelImages, base64Image] 
        };
        onChange('images', updatedImages);
      }
      
      console.log(`[MARKETING] ${imageType} image converted and saved successfully`);
      
    } catch (error) {
      console.error('[MARKETING] Upload error:', error);
      alert(`Fehler beim Bildupload: ${error}`);
    } finally {
      setUploadingImage(null);
    }
  };

  // Image display component
  const ImageUploadArea: React.FC<{ 
    type: 'main' | 'back' | 'label'; 
    currentImages?: string | string[]; 
    label: string;
    allowMultiple?: boolean;
  }> = ({ type, currentImages, label, allowMultiple = false }) => {
    
    const getInputRef = () => {
      switch(type) {
        case 'main': return mainImageInputRef;
        case 'back': return backImageInputRef;
        case 'label': return labelImageInputRef;
      }
    };

    // Get images from data.images object
    const getImagesFromData = () => {
      if (!data?.images) return [];
      
      if (type === 'main') {
        return data.images.mainImage ? [data.images.mainImage] : [];
      } else if (type === 'back') {
        return data.images.backImages || [];
      } else if (type === 'label') {
        return data.images.labelImages || [];
      }
      return [];
    };

    const imageArray = getImagesFromData();
    const hasImages = imageArray.length > 0;

    const removeImage = (indexToRemove: number) => {
      if (type === 'main') {
        const updatedImages = { 
          ...(data.images || {}), 
          mainImage: '' 
        };
        onChange('images', updatedImages);
      } else if (type === 'back') {
        const currentImages = data.images?.backImages || [];
        const updatedImages = currentImages.filter((_: string, index: number) => index !== indexToRemove);
        const newImagesObj = { 
          ...(data.images || {}), 
          backImages: updatedImages 
        };
        onChange('images', newImagesObj);
      } else if (type === 'label') {
        const currentImages = data.images?.labelImages || [];
        const updatedImages = currentImages.filter((_: string, index: number) => index !== indexToRemove);
        const newImagesObj = { 
          ...(data.images || {}), 
          labelImages: updatedImages 
        };
        onChange('images', newImagesObj);
      }
    };

    return (
      <div className="space-y-2">
        <label className="block text-xs font-medium text-gray-700">
          {label} {allowMultiple && '(mehrere möglich)'}
        </label>
        
        {mode === 'edit' ? (
          <div className="space-y-2">
            {/* Upload Area */}
            <div 
              className="border-2 border-dashed border-gray-300 rounded-lg h-32 flex flex-col items-center justify-center hover:border-orange-400 transition-colors cursor-pointer"
              onClick={() => getInputRef().current?.click()}
            >
              {uploadingImage === type ? (
                <div className="text-center">
                  <FontAwesomeIcon icon={faUpload} className="text-2xl text-orange-500 mb-2 animate-pulse" />
                  <p className="text-xs text-gray-600">Wird verarbeitet...</p>
                </div>
              ) : (
                <div className="text-center p-4">
                  <FontAwesomeIcon icon={faPlus} className="text-xl text-gray-400 mb-2" />
                  <p className="text-xs text-gray-600">
                    {hasImages ? (allowMultiple ? 'Weiteres Bild' : 'Bild ersetzen') : 'Bild hinzufügen'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Klicken zum Hochladen</p>
                </div>
              )}
            </div>
            
            {/* Display current images */}
            {hasImages && (
              <div className={`grid gap-2 ${allowMultiple ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {imageArray.map((image: string, index: number) => (
                  <div key={index} className="relative group">
                    <div className="h-24 rounded-lg border border-gray-200 overflow-hidden bg-white">
                      <img 
                        src={image} 
                        alt={`${label} ${index + 1}`} 
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          console.error('Failed to load image:', image.substring(0, 50) + '...');
                        }}
                      />
                    </div>
                    <button
                      onClick={() => removeImage(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          // View mode
          <div className="space-y-2">
            {hasImages ? (
              <div className={`grid gap-2 ${allowMultiple && imageArray.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {imageArray.map((image: string, index: number) => (
                  <div key={index} className="h-32 rounded-lg border border-gray-200 overflow-hidden bg-white">
                    <img 
                      src={image} 
                      alt={`${label} ${index + 1}`} 
                      className="w-full h-full object-contain"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-32 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center">
                <p className="text-xs text-gray-500">Kein Bild</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Produktbilder */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {renderFieldWithHistory('Hauptbild', 'images.mainImage', (
            <ImageUploadArea 
              type="main" 
              label="Hauptbild"
              allowMultiple={false}
            />
          ))}
          {renderFieldWithHistory('Rückseite', 'images.backImages', (
            <ImageUploadArea 
              type="back" 
              label="Rückseite"
              allowMultiple={true}
            />
          ))}
          {renderFieldWithHistory('Etikett', 'images.labelImages', (
            <ImageUploadArea 
              type="label" 
              label="Etikett"
              allowMultiple={true}
            />
          ))}
        </div>
      </div>

      {/* Marketing-Text */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Marketingbotschaft des Artikels
          </label>
          {renderFieldWithHistory('Marketingbotschaft des Artikels', 'marketingMessage', (
            mode === 'edit' ? (
              <textarea
                value={data?.marketingMessage || ''}
                onChange={(e) => onChange('marketingMessage', e.target.value)}
                rows={6}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Geben Sie hier die Marketing-Botschaft für den Artikel ein..."
              />
            ) : (
              <div className="text-sm text-gray-900 p-4 bg-gray-50 rounded-lg min-h-[150px] whitespace-pre-line">
                {data?.marketingMessage || 'Keine Marketing-Botschaft hinterlegt'}
              </div>
            )
          ))}
        </div>
      </div>

      {/* Hidden file inputs */}
      <input
        ref={mainImageInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleImageUpload(file, 'main');
        }}
        className="hidden"
      />
      
      <input
        ref={backImageInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleImageUpload(file, 'back');
        }}
        className="hidden"
      />
      
      <input
        ref={labelImageInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleImageUpload(file, 'label');
        }}
        className="hidden"
      />
      
    </div>
  );
};

export default ArticleDetailMarketing;